import * as ImagePicker from "expo-image-picker";
import { useRef } from "react";
import { Platform } from "react-native";
import { YStack } from "tamagui";

import { Button, Footer, Heading, Paragraph, Text } from "@/components";
import { isMobileWeb } from "@/utils/web";

interface PrescriptionScanProps {
  onPhotoCaptured: (imageUri: string) => void;
}

// OpenAI supported image formats — excludes HEIC/HEIF
const WEB_ACCEPTED_IMAGE_TYPES = "image/jpeg,image/png,image/gif,image/webp";

export const PrescriptionScan = ({
  onPhotoCaptured,
}: PrescriptionScanProps) => {
  const isDesktop = !isMobileWeb();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleOpenCamera = async () => {
    if (Platform.OS === "web" && isDesktop) {
      fileInputRef.current?.click();
      return;
    }
    // On mobile web, launchCameraAsync triggers the native camera via the
    // browser's <input capture="camera">. On desktop it falls back to file picker.
    let result: ImagePicker.ImagePickerResult;
    try {
      result = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        quality: 1,
      });
    } catch {
      // The picker rejects when a non-media file (e.g. a PDF) is selected
      // through the browser's file dialog instead of the camera.
      alert("Please upload a JPEG, PNG, GIF, or WebP image.");
      return;
    }

    if (!result.canceled && result.assets[0]) {
      onPhotoCaptured(result.assets[0].uri);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const accepted = WEB_ACCEPTED_IMAGE_TYPES.split(",");
    if (!accepted.includes(file.type)) {
      alert(
        `Unsupported file type: ${file.type || "unknown"}. Please upload a JPEG, PNG, GIF, or WebP image.`,
      );
      e.target.value = "";
      return;
    }
    const uri = URL.createObjectURL(file);
    onPhotoCaptured(uri);
  };

  return (
    <YStack flex={1} gap="$200" padding="$200">
      <YStack gap="$100" flex={1}>
        <Heading size="$xl">Take a photo of your prescription label</Heading>
        <Paragraph color="$textSecondary" size="$s">
          Please make sure the photo clearly shows as many of these details as
          possible—no glare or shadows. Don't worry if you can't get all of
          these in one photo.
        </Paragraph>
        <YStack gap="$75" paddingTop="$100">
          <Text size="$s" color="$textSecondary">
            • Your full name
          </Text>
          <Text size="$s" color="$textSecondary">
            • Your prescription (drug) name and strength
          </Text>
          <Text size="$s" color="$textSecondary">
            • The drug directions
          </Text>
          <Text size="$s" color="$textSecondary">
            • The prescription (Rx) number
          </Text>
          <Text size="$s" color="$textSecondary">
            • The pharmacy name
          </Text>
          <Text size="$s" color="$textSecondary">
            • Date of issue
          </Text>
        </YStack>
      </YStack>

      {Platform.OS === "web" && isDesktop && (
        <input
          ref={fileInputRef}
          type="file"
          accept={WEB_ACCEPTED_IMAGE_TYPES}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      )}
      <Footer mode="dialog">
        <Button
          mode="primary"
          constrained
          onPress={handleOpenCamera}
          dataTrackingId="prescription-open-camera-button"
        >
          {isDesktop ? "Upload photo" : "Open camera"}
        </Button>
      </Footer>
    </YStack>
  );
};
